import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@personic/shared'

export interface AdminAttestation {
  id: string
  tenant_id: string
  tenant_name: string | null
  tenant_plan: string | null
  framework: string
  status: string
  attested_by_id: string | null
  attested_by_name: string
  attested_by_title: string
  attested_by_email: string | null
  signature_type: string
  signature_data: string
  attestation_data: Record<string, unknown>
  ip_address: string
  submitted_at: string
  reviewed_at: string | null
  reviewed_by_id: string | null
  review_notes: string | null
  expires_at: string
  is_expired: boolean
  is_active: boolean
  days_until_expiry: number
  days_pending: number
  created_at: string
  updated_at: string | null
}

interface AdminAttestationListResponse {
  attestations: AdminAttestation[]
  total: number
  total_pending: number
  oldest_pending_days: number
}

interface AttestationActionResponse {
  id: string
  status: string
  message: string
}

interface AttestationQueueStats {
  total_pending: number
  oldest_pending_days: number
  priority_count: number
  by_framework: Record<string, number>
}

interface UsePendingAttestationsOptions {
  framework?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  limit?: number
  offset?: number
}

/**
 * Fetch pending attestations for review queue
 */
export function usePendingAttestations(options: UsePendingAttestationsOptions = {}) {
  const { framework, sortBy = 'submitted_at', sortOrder = 'asc', limit = 50, offset = 0 } = options

  return useQuery<AdminAttestationListResponse>({
    queryKey: ['admin-pending-attestations', { framework, sortBy, sortOrder, limit, offset }],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (framework) params.append('framework', framework)
      params.append('sort_by', sortBy)
      params.append('sort_order', sortOrder)
      params.append('limit', String(limit))
      params.append('offset', String(offset))

      const res = await api.get(`/admin/attestations/queue?${params.toString()}`)
      return res.data
    },
  })
}

/**
 * Fetch single attestation for review
 */
export function useAttestationForReview(id: string | null) {
  return useQuery<AdminAttestation>({
    queryKey: ['admin-attestation', id],
    queryFn: async () => {
      const res = await api.get(`/admin/attestations/${id}`)
      return res.data
    },
    enabled: !!id,
  })
}

/**
 * Fetch attestation queue stats
 */
export function useAttestationQueueStats() {
  return useQuery<AttestationQueueStats>({
    queryKey: ['admin-attestation-stats'],
    queryFn: async () => {
      const res = await api.get('/admin/attestations/stats')
      return res.data
    },
  })
}

/**
 * Approve attestation mutation
 */
export function useApproveAttestation() {
  const queryClient = useQueryClient()

  return useMutation<AttestationActionResponse, Error, { id: string; notes?: string }>({
    mutationFn: async ({ id, notes }) => {
      const res = await api.post(`/admin/attestations/${id}/approve`, { notes })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-pending-attestations'] })
      queryClient.invalidateQueries({ queryKey: ['admin-attestation-stats'] })
    },
  })
}

/**
 * Reject attestation mutation
 */
export function useRejectAttestation() {
  const queryClient = useQueryClient()

  return useMutation<AttestationActionResponse, Error, { id: string; notes: string }>({
    mutationFn: async ({ id, notes }) => {
      const res = await api.post(`/admin/attestations/${id}/reject`, { notes })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-pending-attestations'] })
      queryClient.invalidateQueries({ queryKey: ['admin-attestation-stats'] })
    },
  })
}

/**
 * Request changes mutation
 */
export function useRequestAttestationChanges() {
  const queryClient = useQueryClient()

  return useMutation<AttestationActionResponse, Error, { id: string; notes: string }>({
    mutationFn: async ({ id, notes }) => {
      const res = await api.post(`/admin/attestations/${id}/request-changes`, { notes })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-pending-attestations'] })
      queryClient.invalidateQueries({ queryKey: ['admin-attestation-stats'] })
    },
  })
}
